import { lowerCase } from 'lodash/fp';
import { ComponentProps, FC, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom-v5-compat';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Inquiry } from 'dashboard/models/inquiry';
import { WorkflowRunSimulated } from 'dashboard/models/workflow-run-simulated';
import { showMessage } from 'dashboard/redux/actions/messages';
import { clearWorkflowRun } from 'dashboard/redux/actions/workflow-run';
import { RootState } from 'dashboard/redux/store';
import {
  loadWorkflowRun,
  loadWorkflowRunLoopChildrenSteps,
} from 'dashboard/redux/thunk-actions/workflow-run';
import {
  loadFilterFieldConfigs,
  loadWorkflowSchemaParallel,
} from 'dashboard/redux/thunk-actions/workflow-schema';
import DashboardWorkflowSimulatedRunsShowRoute from 'dashboard/routes/DashboardWorkflowRunsShowRoute/DashboardWorkflowSimulatedRunsShowRoute';
import DashboardWorkflowRunsShowView from 'dashboard/views/DashboardWorkflowRunsShowView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiLoader from 'lib/hooks/useApiLoader';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

enum RunType {
  Workflow = 'workflow_run',
  CaseAction = 'case_action_run',
  AccountAction = 'account_action_run',
}

interface DashboardWorkflowsShowOwnProps {
  workflowVersionId: string;
}

export type DashboardWorkflowRunsShowRouteProps = PropsFromRedux & DashboardWorkflowsShowOwnProps;

type Params = {
  workflowId?: string;
  workflowRunId: string;
  accountTypeId?: string;
  accountActionId?: string;
};

const DashboardWorkflowRunsShowRoute: FC<DashboardWorkflowRunsShowRouteProps> = (props) => {
  const {
    workflow,
    workflowVersion,
    workflowRun,
    inputStep,
    steps,
    loopChildrenSteps,
    moduleRunStepsByConfigStepToken,
    workflowSchema,
    knownObjects,
    triggerObject,
  } = props;

  useOnMountAndUnmount(props.clearWorkflowRun);

  const { workflowId, workflowRunId, accountTypeId, accountActionId } =
    useParams<Params>() as Params; // workflowRunId will always be defined
  const navigate = useNavigate();
  const location = useLocation();

  const runType: RunType = useMemo(() => {
    if (location.pathname?.startsWith('/dashboard/account-types')) {
      return RunType.AccountAction;
    }
    if (location.pathname?.startsWith('/dashboard/case-actions')) {
      return RunType.CaseAction;
    }
    return RunType.Workflow;
  }, [location.pathname]);

  const replaceRoute = useMemo(() => {
    switch (runType) {
      case RunType.Workflow:
        return `/dashboard/workflows/${workflowId}/runs`;
      case RunType.AccountAction:
        return `/dashboard/account-types/${accountTypeId}/actions/${accountActionId}/runs`;
      case RunType.CaseAction:
        return `/dashboard/case-actions/${workflowId}/runs`;
    }
  }, [runType, workflowId, accountTypeId, accountActionId]);

  const { callback: handleLoadLoopChildrenSteps, loading: isLoadingLoopCycle } = useApiCallback(
    async (workflowRunId: string, loopStepId: string, loopIterationIdx: number) => {
      await props.loadWorkflowRunLoopChildrenSteps(workflowRunId, loopStepId, loopIterationIdx);
    },
    [workflowRunId],
  );

  const enablePlatformObjectJsonSchema = useFeatureFlag(
    'dashboard:workflow-use-platform-object-json-schema',
  );
  const { loading } = useApiLoader({
    loader: async () => {
      await props.loadWorkflowSchemaParallel({
        loadCriteriaVariableSchemas: !enablePlatformObjectJsonSchema,
      });
      await props.loadWorkflowRun(workflowRunId, navigate);
    },
    onError: (error) => {
      if (isPermissionsError(error)) {
        navigate(replaceRoute, { replace: true });
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        navigate(replaceRoute, { replace: true });
        props.showMessage(
          `Failed to load ${lowerCase(runType)}, please try again`,
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        `Failed to load ${lowerCase(runType)}, please try again`,
        MessageType.Failure,
      ),
    deps: [
      workflowRunId,
      replaceRoute,
      runType,
      props.loadWorkflowRun,
      props.loadWorkflowSchemaParallel,
      props.showMessage,
    ],
  });

  if (
    loading ||
    workflowVersion == null ||
    workflow == null ||
    workflowRun == null ||
    inputStep == null
  ) {
    return (
      <Backdrop>
        <LoadingIndicator fullscreen={true} />
      </Backdrop>
    );
  } else {
    if (workflowRun.type === ApiModel.WorkflowRunSimulated) {
      switch (runType) {
        case RunType.AccountAction:
          return (
            <Backdrop>
              <NullState
                message='Simulate is unsupported for account actions at the moment'
                backRoute='/dashboard/accounts'
              />
            </Backdrop>
          );
        case RunType.CaseAction:
          return (
            <Backdrop>
              <NullState
                message='Simulate is unsupported for case actions at the moment'
                backRoute='/dashboard/case-actions'
              />
            </Backdrop>
          );
        default:
          return (
            <DashboardWorkflowSimulatedRunsShowRoute
              {...props}
              workflowRun={workflowRun as WorkflowRunSimulated}
            />
          );
      }
    }

    const runsShowViewProps: Omit<
      ComponentProps<typeof DashboardWorkflowRunsShowView>,
      'editorRoute' | 'backRoute' | 'helmetTitleSuffix'
    > = {
      workflowVersion,
      workflow,
      workflowRun,
      inputStep,
      steps,
      loopChildrenSteps,
      moduleRunStepsByConfigStepToken,
      workflowSchema,
      knownObjects,
      triggerObject,
      isLoadingLoopCycle,
      handleLoadLoopChildrenSteps,
    };

    const latestWorkflowVersionId =
      workflow.relationships['latest-draft-version']?.data?.id ??
      workflow.relationships['latest-published-version']?.data?.id ??
      workflowVersion.id;

    switch (runType) {
      case RunType.AccountAction:
        return (
          <DashboardWorkflowRunsShowView
            {...runsShowViewProps}
            backRoute={`/dashboard/account-types/${accountTypeId}/actions/${accountActionId}/runs`}
            editorRoute={{
              label: 'Go to Action',
              path: `/dashboard/account-types/${accountTypeId}/actions/${accountActionId}/${latestWorkflowVersionId}`,
            }}
            helmetTitleSuffix='Actions - Accounts'
          />
        );
      case RunType.CaseAction:
        return (
          <DashboardWorkflowRunsShowView
            {...runsShowViewProps}
            backRoute={`/dashboard/case-actions/${workflow.id}/runs`}
            editorRoute={{
              label: 'Go to Action',
              path: `/dashboard/case-actions/${workflow.id}/${latestWorkflowVersionId}`,
            }}
            helmetTitleSuffix='Cases'
          />
        );
      default: {
        const routesOverwrite: Pick<
          ComponentProps<typeof DashboardWorkflowRunsShowView>,
          'editorRoute' | 'backRoute'
        > = (() => {
          if (inputStep.trigger?.type === ApiModel.TriggerDirect && triggerObject) {
            const {
              'inquiry-template': inquiryTemplate,
              'inquiry-template-version': inquiryTemplateVersion,
            } = (triggerObject as Inquiry).relationships;
            const inquiryTemplateId = inquiryTemplate?.data?.id;
            const inquiryTemplateVersionId = inquiryTemplateVersion?.data?.id;
            if (inquiryTemplateId != null && inquiryTemplateVersionId != null) {
              return {
                editorRoute: null,
                backRoute: `/dashboard/inquiry-templates/${inquiryTemplateId}/${inquiryTemplateVersionId}/workflow-runs`,
              };
            }
          }

          return {
            backRoute: `/dashboard/workflows/${workflow.id}/runs`,
            editorRoute: {
              label: 'Go to Workflow',
              path: `/dashboard/workflows/${workflow.id}/${latestWorkflowVersionId}`,
            },
          };
        })();

        return (
          <DashboardWorkflowRunsShowView
            {...runsShowViewProps}
            {...routesOverwrite}
            helmetTitleSuffix='Workflows'
          />
        );
      }
    }
  }
};

const mapStateToProps = (state: RootState) => ({
  currentEnvironment: state.currentEnvironment.currentEnvironment, // used for simulate
  environments: state.organization.environments, // used for simulate
  workflow: state.workflow.present.workflow,
  workflowVersion: state.workflow.present.workflowVersion,
  workflowRun: state.workflowRun.workflowRun,
  triggerObject: state.workflowRun.triggerObject,
  steps: state.workflowRun.steps,
  loopChildrenSteps: state.workflowRun.loopChildrenSteps,
  moduleRunStepsByConfigStepToken: state.workflowEditor.moduleRunStepsByConfigStepToken,
  inputStep: state.workflow.present.inputStep,
  knownObjects: state.workflowRun.knownObjects,
  workflowSchema: state.workflowSchema,
});

const mapDispatchToProps = {
  clearWorkflowRun,
  loadFilterFieldConfigs,
  loadWorkflowRun,
  loadWorkflowRunLoopChildrenSteps,
  loadWorkflowSchemaParallel,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardWorkflowRunsShowRoute);
